import {
  Fragment,
  useState,
} from 'react';
import {
  Callout,
  Confirm,
  Field,
  Modal,
  Select,
  Chip,
  Empty,
  Loading,
  Panel,
  TableScroll,
  explainFailure,
  stripRuleReferences,
  useLoad,
  ScreenWithSections,
  rowOpen,
} from '@affdice/portal-kit';
import {
  PlatformApi,
} from '../../client.ts';
import { Failure } from '../shared.tsx';
import { t, tCount } from '../../messages.ts';
import { ALL, flagSections, type FlagRow } from './feature-flags-model.ts';

/**
 * Item 8 — one flag so far (TOTP), built so a second one needs no new
 * component. Turning a flag ON→OFF is direct (the server clears every
 * override either way); turning it OFF→ON opens the operator-picker below
 * rather than switching every operator on — that decision belongs to
 * whoever is running this screen, not to a default.
 */


/**
 * Item 18 (third batch) — a compact table, not one full card per flag.
 *
 * Every flag rendered its whole per-operator override editor inline, always
 * expanded, so a screen with a handful of flags was mostly the editor for
 * flags nobody was looking at. The list now answers the question the list is
 * for — what exists, and is it on — and the overrides move behind a row
 * click, the same pattern the Operators and Affiliates lists use.
 */
export function FeatureFlags({ api, canWrite }: { api: PlatformApi; canWrite: boolean }) {
  const [version, setVersion] = useState(0);
  const flags = useLoad(() => api.featureFlags(), [version]);
  const operators = useLoad(() => api.operators(), []);
  const [failure, setFailure] = useState<unknown>(null);
  const [open, setOpen] = useState<string | null>(null);
  /*
   * Owner, 2026-10-07 — flags by section. A flag's section is the screen it
   * governs (`surface`, migration 0172), so the sections come from the data
   * and a new flag lands in its own without a change here. ALL shows every
   * section, each as its own table.
   */
  const [section, setSection] = useState<string>(ALL);
  const operatorList = operators.state === 'ready' ? operators.value.items : [];
  const allFlags = flags.state === 'ready' ? flags.value.items : [];
  const openFlag = allFlags.find((f) => f.code === open);
  const sections = flagSections(allFlags);
  const shown = section === ALL ? sections : sections.filter((g) => g.id === section);

  const body = (
    <>
      {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}
      {flags.state === 'loading' ? <Loading rows={2} /> : null}
      {flags.state === 'error' ? <Failure error={flags.error} /> : null}
      {flags.state === 'ready' && allFlags.length === 0 ? <Empty>{t('plat.flags.empty')}</Empty> : null}
      {shown.map((g) => (
        <Panel key={g.id} title={g.label} aside={<span className="count count-badge">{g.flags.length}</span>}>
          <FlagTable flags={g.flags} onOpen={setOpen} />
        </Panel>
      ))}
    </>
  );

  return (
    <>
      {sections.length > 1 ? (
        <ScreenWithSections
          title={t('nav.featureFlags')}
          ariaLabel={t('plat.flags.sections')}
          section={section}
          onSection={setSection}
          groups={[{
            label: t('plat.flags.panel'),
            items: [
              { id: ALL, label: t('plat.flags.allSections', { n: allFlags.length }) },
              ...sections.map((g) => ({ id: g.id, label: `${g.label} (${g.flags.length})` })),
            ],
          }]}
        >
          {body}
        </ScreenWithSections>
      ) : (
        <>
          <div className="page-head">
            <h1>{t('nav.featureFlags')}</h1>
          </div>
          {body}
        </>
      )}

      {openFlag ? (
        <Modal wide title={openFlag.label} onClose={() => setOpen(null)}>
          <FeatureFlagPanel
            api={api}
            flag={openFlag}
            operators={operatorList}
            canWrite={canWrite}
            onChanged={() => setVersion((v) => v + 1)}
            onFailure={setFailure}
          />
        </Modal>
      ) : null}
    </>
  );
}


/**
 * Item 18 (third batch) — a compact table, not one full card per flag; the
 * overrides open behind a row click, the same pattern the Operators and
 * Affiliates lists use. The section is the panel's title, so no column says
 * it again.
 */
function FlagTable({ flags, onOpen }: { flags: readonly FlagRow[]; onOpen: (code: string) => void }) {
  return (
    <TableScroll>
      <table>
        <thead>
          <tr>
            <th>{t('plat.flags.colFlag')}</th><th>{t('plat.flags.colDefault')}</th>
            <th className="num">{t('plat.flags.colOverrides')}</th>
          </tr>
        </thead>
        <tbody>
          {/* Batch 2 item 13 — "What it does" ran to a paragraph and
              stretched every row; it is a row note below, spanning the
              three columns above. */}
          {flags.map((flag) => {
            const openThis = () => onOpen(flag.code);
            const description = stripRuleReferences(flag.description);
            return (
              <Fragment key={flag.code}>
                <tr className={description ? 'row-clickable has-note' : 'row-clickable'} onClick={openThis} {...rowOpen(openThis)}>
                  <td>{flag.label}</td>
                  <td>
                    {/* Batch 2 item 6 — off is neutral, not the provisional
                        ring — nothing here is money that can still move. */}
                    <Chip tone={flag.global_enabled ? 'locked' : 'neutral'}>
                      {flag.global_enabled ? t('plat.flags.on') : t('plat.flags.off')}
                    </Chip>
                  </td>
                  {/* The count, not the list. */}
                  <td className="num">{flag.overrides.length || '—'}</td>
                </tr>
                {description ? (
                  <tr className="row-note row-clickable" onClick={openThis}>
                    <td colSpan={3}><span className="row-note-text">{description}</span></td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </TableScroll>
  );
}

function FeatureFlagPanel({
  api,
  flag,
  operators,
  canWrite,
  onChanged,
  onFailure,
}: {
  api: PlatformApi;
  flag: {
    code: string;
    label: string;
    description: string;
    global_enabled: boolean;
    overrides: { operator_id: string; operator: string; enabled: boolean }[];
  };
  operators: { id: string; display_name: string }[];
  /** Item 4 (third batch) — false for the view-only PLATFORM_USER tier. */
  canWrite: boolean;
  onChanged: () => void;
  onFailure: (e: unknown) => void;
}) {
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [addOperatorId, setAddOperatorId] = useState('');
  const [addEnabled, setAddEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  // Batch 2 item 3 — turning the default off used to write on the same
  // click; it now confirms first, naming who it reaches.
  const [confirmingOff, setConfirmingOff] = useState(false);
  // Batch 2 item 3 — which override's "Clear override" is being confirmed.
  const [clearing, setClearing] = useState<{ operator_id: string; operator: string } | null>(null);

  const overriddenIds = new Set(flag.overrides.map((o) => o.operator_id));
  const addableOperators = operators.filter((o) => !overriddenIds.has(o.id));
  // Batch 2 review — who is actually on right now, for the "Turn off"
  // confirmation: an operator with no override follows the (on) default;
  // one with an override is on only if that override says so.
  const onOperators = operators.filter((o) => {
    const override = flag.overrides.find((ov) => ov.operator_id === o.id);
    return override ? override.enabled : true;
  });

  // Item 18 (third batch) — this is the modal's body now, so it carries no
  // Panel/title of its own: the modal header already names the flag.
  return (
    <div className="stack">
      <p className="offer-note">{stripRuleReferences(flag.description)}</p>

      {/* Batch 2 item 1 — a read-only role never sees this control at all,
          rather than seeing it disabled: the banner promises actions are
          hidden, not shown and refused. */}
      {canWrite ? (
        <div className="form-actions">
          <button
            type="button"
            className="copy"
            disabled={busy}
            onClick={() => {
              if (flag.global_enabled) {
                setConfirmingOff(true);
                return;
              }
              setPicked(new Set());
              setPicking(true);
            }}
          >
            {flag.global_enabled ? t('plat.flags.turnOffAll') : t('plat.flags.turnOnAll')}
          </button>
        </div>
      ) : (
        <p className="panel-note">
          {flag.global_enabled ? t('plat.flags.readOnlyOn') : t('plat.flags.readOnlyOff')}
        </p>
      )}

      {/* Batch 2 item 3 — "one click switches a platform-wide behaviour
          off" was this exact control: unchecking it wrote straight away,
          with no step in between. */}
      {/* Batch 2 review, finding 1 — the old copy said an override keeps an
          operator on. It does not: setGlobalFlag(code, false) deletes every
          override for this flag along with flipping the default, so an
          operator switched on by its own override goes off exactly like one
          that was only following the default. Say what actually happens,
          and name who is on right now rather than only who has no override. */}
      {confirmingOff ? (
        <Confirm
          title={t('plat.flags.turnOffTitle', { flag: flag.label })}
          tone="error"
          confirmLabel={t('plat.flags.turnOffConfirm')}
          body={
            onOperators.length === 0 ? (
              <>
                {t('plat.flags.offNobodyOn')}{' '}
                {flag.overrides.length > 0 ? (
                  <>{tCount('plat.flags.offNobodyOnDeletes', flag.overrides.length)}</>
                ) : null}
              </>
            ) : (
              <>
                {tCount('plat.flags.offEveryone', onOperators.length, {
                  names: onOperators.map((o) => o.display_name).join(', '),
                })}
                {flag.overrides.length > 0 ? (
                  <>
                    {' '}{tCount('plat.flags.offEveryoneDeletes', flag.overrides.length)}
                  </>
                ) : null}
              </>
            )
          }
          onCancel={() => setConfirmingOff(false)}
          onConfirm={async () => {
            await api.setFeatureFlagGlobal(flag.code, false);
            setConfirmingOff(false);
            onChanged();
          }}
        />
      ) : null}

      {picking ? (
        <Modal title={t('plat.flags.pickTitle', { flag: flag.label })} onClose={() => setPicking(false)}>
          {/* Batch 2 review, finding 1/3 — setGlobalFlagWithSelection writes
              every current operator an explicit override (on for who is
              picked, off for who is not), overwriting any override they
              already held either way; it does not leave anyone "as they
              were". Picking nobody is a real, offered case — the flag then
              only reaches an operator created after today, since every
              existing one is written an explicit off. */}
          <Callout fold={false}>
            {t('plat.flags.pickNote')}
          </Callout>
          <div className="checkbox-stack">
            {operators.map((o) => (
              <label key={o.id} className="checkbox-stack-row">
                <input
                  type="checkbox"
                  checked={picked.has(o.id)}
                  onChange={(e) =>
                    setPicked((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(o.id); else next.delete(o.id);
                      return next;
                    })
                  }
                />
                {' '}{o.display_name}
              </label>
            ))}
          </div>
          <div className="form-actions">
            <button
              className="primary"
              // Batch 2 review, finding 3 — 0 picked is a legitimate case
              // (on by default for new operators only), not "nothing to do":
              // re-enabled, with a label that says exactly what it does.
              disabled={busy}
              onClick={() => {
                setBusy(true);
                api
                  .setFeatureFlagGlobalWithSelection(flag.code, [...picked])
                  .then(() => { setPicking(false); onChanged(); })
                  .catch(onFailure)
                  .finally(() => setBusy(false));
              }}
            >
              {picked.size === 0
                ? t('plat.flags.turnOnNewOnly')
                : tCount('plat.flags.turnOnFor', picked.size)}
            </button>
            <button className="copy" onClick={() => setPicking(false)}>{t('common.cancel')}</button>
          </div>
        </Modal>
      ) : null}

      {flag.overrides.length === 0 ? (
        <Empty>{t('plat.flags.noOverrides')}</Empty>
      ) : (
        <TableScroll>
          <table>
            <thead><tr><th>{t('common.operator')}</th><th>{t('plat.flags.colSetting')}</th><th /></tr></thead>
            <tbody>
              {flag.overrides.map((o) => (
                <tr key={o.operator_id}>
                  <td>{o.operator}</td>
                  <td>
                    {/* Batch 2 item 6 — off is neutral, not provisional. */}
                    <Chip tone={o.enabled ? 'locked' : 'neutral'}>{o.enabled ? t('plat.flags.on') : t('plat.flags.off')}</Chip>
                  </td>
                  <td className="num">
                    {canWrite ? (
                      <button
                        className="link-button"
                        onClick={() => setClearing({ operator_id: o.operator_id, operator: o.operator })}
                      >
                        {t('plat.flags.clearLink')}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}

      {clearing ? (
        <Confirm
          title={t('plat.flags.clearTitle', { operator: clearing.operator })}
          confirmLabel={t('plat.flags.clearConfirm')}
          body={
            <>
              {t('plat.flags.clearBodyBefore', { operator: clearing.operator, flag: flag.label })}
              <strong>{flag.global_enabled ? t('plat.flags.clearBodyOn') : t('plat.flags.clearBodyOff')}</strong>
              {t('plat.flags.clearBodyAfter')}
            </>
          }
          onCancel={() => setClearing(null)}
          onConfirm={async () => {
            await api.clearFeatureFlagOperator(flag.code, clearing.operator_id);
            setClearing(null);
            onChanged();
          }}
        />
      ) : null}

      {canWrite && addableOperators.length > 0 ? (
        <div className="form-row">
          <Field label={t('plat.flags.exemptLabel')}>
            <Select
              value={addOperatorId}
              onChange={setAddOperatorId}
              options={[
                { value: '', label: t('plat.flags.chooseOperator') },
                ...addableOperators.map((o) => ({ value: o.id, label: o.display_name })),
              ]}
            />
          </Field>
          <Field label={t('plat.flags.settingFor')}>
            <Select
              value={addEnabled ? 'on' : 'off'}
              onChange={(v) => setAddEnabled(v === 'on')}
              options={[{ value: 'on', label: t('plat.flags.on') }, { value: 'off', label: t('plat.flags.off') }]}
            />
          </Field>
          <button
            className="copy"
            disabled={!addOperatorId || busy}
            onClick={() => {
              setBusy(true);
              api
                .setFeatureFlagOperator(flag.code, addOperatorId, addEnabled)
                .then(() => { setAddOperatorId(''); onChanged(); })
                .catch(onFailure)
                .finally(() => setBusy(false));
            }}
          >
            {t('plat.flags.setOverride')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
